<template>
  <div class="ws">
    <div v-if="!words.length" class="dempty">
      Ни одно слово не проходит порог «мин. сессий со словом».
    </div>
    <template v-else>
    <div class="ws-axis">
      <span></span>
      <div class="ws-ax">
        <span style="left:0">0</span>
        <span style="left:50%">50</span>
        <span style="left:100%; transform:translateX(-100%)">100 %</span>
      </div>
      <span class="ws-medlabel">мед.</span>
    </div>

    <!-- Один слушатель на все оси вместо пары на каждую точку: при сотне слов
         и десятке блоков это тысячи обработчиков. -->
    <div class="ws-rows" @mousemove="onMove2" @mouseleave="onLeave">
      <div v-for="(block, bi) in blocks" :key="bi" class="ws-row">
        <div class="ws-name" :title="block">{{ block }}</div>
        <div class="ws-strip" :data-bi="bi" @mousedown="onDown($event, bi)">
          <div v-if="iqr && quarts[bi]" class="ws-iqr" :style="iqrStyle(bi)"></div>
          <div class="ws-medtick" :style="{ left: pos(medians[bi]) }"></div>
          <div class="ws-medval num" :style="{ left: pos(medians[bi]) }">{{ fmt(medians[bi]) }}</div>
          <div
            v-for="(w, wj) in words" :key="w.wi"
            class="ws-dot"
            :class="dotClass(w, bi)"
            :style="dotStyle(w, bi, wj)"
            :data-idx="wj"
            :data-bi="bi"
            :aria-label="w.word + ', ' + block + ': ' + fmt(pick(w.cells[bi]))"
            @click.stop="onDot(w, bi)"
          ></div>
          <div v-if="drag && drag.bi === bi" class="ws-brush" :style="brushStyle"></div>
        </div>
        <div class="ws-medcol num">{{ fmt(medians[bi]) }}</div>
      </div>
    </div>
    </template>

    <div class="ws-tip" ref="tip" style="display:none">
      <div class="ws-tip-word"></div>
      <div class="num ws-tip-val"></div>
      <div class="num ws-tip-sub"></div>
    </div>
  </div>
</template>

<script>
import { devStep } from '@/utils/wordMatrix';

export default {
  name: 'WordStrips',
  props: {
    blocks: { type: Array, required: true },
    words: { type: Array, required: true },
    medians: { type: Array, required: true },
    quarts: { type: Array, default: () => [] },
    pick: { type: Function, required: true },
    sel: { type: Object, default: null },
    cmp: { type: Boolean, default: false },
    cmpWi: { type: Number, default: null },
    iqr: { type: Boolean, default: false },
    brush: { type: Boolean, default: false },
    brushSel: { type: Object, default: null },
    // «отклонения» работают и здесь: точка красится по отклонению от медианы блока (прототип 7821)
    dev: { type: Boolean, default: false },
  },
  emits: ['select-cell', 'select-word', 'brush'],
  data() {
    // hoverWi/hoverBi — наведённая точка. Меняются только при переходе на
    // другую точку, поэтому перерисовка идёт не на каждое движение мыши.
    return { drag: null, justBrushed: false, hoverWi: null, hoverBi: null };
  },
  computed: {
    // Наведение подсвечивает слово так же, как выбор, но выбор не заменяет:
    // увели мышь — вернулось то, что было выбрано щелчком.
    effSel() {
      if (this.hoverWi === null) return this.sel;
      return { wi: this.hoverWi, bi: this.hoverBi };
    },
    brushStyle() {
      if (!this.drag) return {};
      const lo = Math.min(this.drag.x0, this.drag.x1);
      const hi = Math.max(this.drag.x0, this.drag.x1);
      return { left: lo + 'px', width: (hi - lo) + 'px' };
    },
  },
  beforeUnmount() {
    this.detach();
  },
  methods: {
    fmt(value) {
      return Math.round((Number(value) || 0) * 100) + ' %';
    },
    pos(value) {
      return Math.min(100, Math.max(0, (Number(value) || 0) * 100)) + '%';
    },
    iqrStyle(bi) {
      const [q1, q3] = this.quarts[bi];
      return { left: this.pos(q1), width: Math.max(0, (q3 - q1) * 100) + '%' };
    },
    dotStyle(w, bi, wj) {
      const v = this.pick(w.cells[bi]);
      const st = {
        left: this.pos(v),
        // Три дорожки по вертикали: иначе близкие значения слипаются в одну точку.
        top: 'calc(50% + ' + ((wj % 3) - 1) * 8 + 'px)',
      };
      if (this.dev) st.background = 'var(--cell-' + devStep((Number(v) || 0) - (this.medians[bi] || 0)) + ')';
      return st;
    },
    dotClass(w, bi) {
      // Диапазон выделяется на одной оси, а гасит слово на всех: смысл
      // выделения в том, чтобы проследить попавшие в него слова по блокам.
      const outOfBrush = !!(this.brushSel && this.brushSel.wis.indexOf(w.wi) === -1);
      const sel = this.effSel;
      const isSel = !!(sel && sel.wi === w.wi);
      // Выбранное слово и оба слова пары сравнения не гасим никогда: иначе
      // выделение диапазона тушило бы ровно то, что пользователь только что
      // выбрал, а второе слово пары было бы приглушено на всех осях.
      const chosen = isSel || (this.cmp && w.wi === this.cmpWi);
      return {
        'is-sel': isSel,
        'is-selx': isSel && sel.bi === bi,
        'is-cmp': this.cmp && w.wi === this.cmpWi,
        'is-dim': !chosen && ((sel && sel.wi !== w.wi) || outOfBrush),
      };
    },
    // Наведение: подсветка точек и мгновенная подсказка. Нативный title
    // появляется с задержкой в секунду — на плотной оси это делает разбор
    // соседних точек невозможным.
    onMove2(event) {
      // Во время протяжки подсветка и подсказка только мешают.
      if (this.drag) return;
      const dot = event.target.closest('.ws-dot');
      if (!dot) { this.onLeave(); return; }
      const wj = Number(dot.dataset.idx);
      const bi = Number(dot.dataset.bi);
      const w = this.words[wj];
      if (!w) { this.onLeave(); return; }
      if (this.hoverWi !== w.wi) this.hoverWi = w.wi;
      if (this.hoverBi !== bi) this.hoverBi = bi;
      this.showTip(event, w, bi);
    },
    onLeave() {
      if (this.hoverWi !== null) { this.hoverWi = null; this.hoverBi = null; }
      const el = this.$refs.tip;
      if (el) el.style.display = 'none';
    },
    // Подсказка обновляется напрямую через DOM: реактивное поле пересчитывало
    // бы класс и стиль всех точек на каждое движение мыши.
    showTip(event, w, bi) {
      const el = this.$refs.tip;
      if (!el) return;
      const v = this.pick(w.cells[bi]);
      const med = this.medians[bi] || 0;
      const diff = Math.round((v - med) * 100);
      el.querySelector('.ws-tip-word').textContent = w.word;
      el.querySelector('.ws-tip-val').textContent = this.blocks[bi] + ' · ' + this.fmt(v);
      el.querySelector('.ws-tip-sub').textContent =
        (diff > 0 ? '+' + diff : diff) + ' к медиане (' + this.fmt(med) + ')';
      el.style.display = 'block';
      const box = el.getBoundingClientRect();
      el.style.left = Math.min(event.clientX + 14, window.innerWidth - box.width - 8) + 'px';
      el.style.top = Math.min(event.clientY + 16, window.innerHeight - box.height - 8) + 'px';
    },
    onDot(w, bi) {
      // После протяжки мышь отпускается над точкой — этот клик игнорируем,
      // иначе выделение тут же сменялось бы выбором одной точки.
      if (this.justBrushed) { this.justBrushed = false; return; }
      if (this.cmp) { this.$emit('select-word', w.wi); return; }
      this.$emit('select-cell', w.wi, bi);
    },
    onDown(event, bi) {
      if (!this.brush) return;
      event.preventDefault();
      // Подсказка перекрывала бы протягиваемый диапазон.
      this.onLeave();
      const strip = event.currentTarget;
      const rect = strip.getBoundingClientRect();
      const x = event.clientX - rect.left;
      this.drag = { bi: bi, strip: strip, rect: rect, x0: x, x1: x };
      window.addEventListener('mousemove', this.onMove);
      window.addEventListener('mouseup', this.onUp);
    },
    onMove(event) {
      if (!this.drag) return;
      // Геометрию перечитываем на каждом шаге: ресайз окна посреди протяжки
      // сделал бы сохранённый прямоугольник неактуальным, и выделенный
      // диапазон разошёлся бы с нарисованным.
      const rect = this.drag.strip.getBoundingClientRect();
      this.drag.rect = rect;
      this.drag.x1 = Math.min(rect.width, Math.max(0, event.clientX - rect.left));
    },
    onUp() {
      if (!this.drag) return;
      const { bi, rect, x1 } = this.drag;
      const x0 = Math.min(rect.width, Math.max(0, this.drag.x0));
      this.detach();
      this.drag = null;
      if (Math.abs(x1 - x0) < 6) { this.$emit('brush', null); return; }
      const lo = Math.min(x0, x1) / rect.width;
      const hi = Math.max(x0, x1) / rect.width;
      const wis = this.words
        .filter(w => { const v = this.pick(w.cells[bi]); return v >= lo && v <= hi; })
        .map(w => w.wi);
      this.justBrushed = true;
      this.$emit('brush', { bi: bi, lo: lo, hi: hi, wis: wis });
      // Клик прилетает сразу после mouseup и только если кнопку отпустили над
      // той же точкой. Если этого не случилось — а протяжка обычно кончается
      // на пустом месте оси — флаг обязан сняться сам, иначе он молча
      // проглотит следующий обычный клик по точке.
      setTimeout(() => { this.justBrushed = false; }, 0);
    },
    detach() {
      window.removeEventListener('mousemove', this.onMove);
      window.removeEventListener('mouseup', this.onUp);
    },
  },
};
</script>

<style scoped>
.ws-axis, .ws-row {
  display: grid;
  grid-template-columns: 150px 1fr 60px;
  gap: 12px;
  align-items: center;
}
.ws-axis { font-size: 10px; color: var(--text-muted); margin-bottom: 4px; }
.ws-ax { position: relative; height: 14px; }
.ws-ax span { position: absolute; transform: translateX(-50%); white-space: nowrap; }
.ws-medlabel { font-size: 10px; }
.ws-rows { display: flex; flex-direction: column; gap: 12px; }
.ws-name {
  font-size: 13px; color: var(--text-secondary); text-align: right;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ws-strip { position: relative; height: 36px; }
.ws-strip::before {
  content: ""; position: absolute; left: 0; right: 0; top: 50%;
  height: 1px; background: var(--grid);
}
.ws-iqr {
  position: absolute; top: 6px; bottom: 6px;
  background: var(--grid); opacity: .6; border-radius: 4px;
}
.ws-medtick {
  position: absolute; top: 3px; bottom: 3px; width: 2px;
  background: var(--text-primary); transform: translateX(-50%);
  border-radius: 1px; z-index: 2; opacity: .75;
}
.ws-medval {
  position: absolute; top: -8px; transform: translateX(-50%);
  font-size: 10px; color: var(--text-secondary);
  background: var(--surface-1); padding: 0 3px; z-index: 2;
}
.ws-dot {
  position: absolute; width: 11px; height: 11px; border-radius: 50%;
  transform: translate(-50%, -50%); background: var(--seq-60);
  box-shadow: 0 0 0 2px var(--surface-1); cursor: pointer; z-index: 1;
}
.ws-dot.is-dim { opacity: .3; }
.ws-dot.is-sel { background: var(--seq-100); width: 14px; height: 14px; z-index: 3; }
.ws-dot.is-selx { outline: 2px solid var(--text-primary); outline-offset: 1px; }
.ws-dot.is-cmp { background: var(--cmp); }
.ws-brush {
  position: absolute; top: 2px; bottom: 2px;
  background: var(--brush-fill); border: 1px solid var(--accent);
  border-radius: 4px; pointer-events: none; z-index: 4;
}
.ws-medcol { font-size: 11.5px; color: var(--text-muted); text-align: left; }
.ws-tip {
  position: fixed;
  z-index: 50;
  background: var(--tooltip-bg);
  color: var(--tooltip-ink);
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 12.5px;
  pointer-events: none;
  max-width: 260px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .25);
}
.ws-tip-word { font-weight: 650; margin-bottom: 2px; }
.ws-tip-sub { opacity: .7; }
</style>
